import { addClass, toggleClass, removeClass } from '@v4/common/utils/dom';

const wrapText = (element, readMore) => {
    const content = element.textContent;

    if (!content.replace(/\s/g, '').length) {
        return '';
    }

    if (readMore.counting >= readMore.count) {
        return `<span class="js-dk-read-more__content">${content}</span>`;
    }

    if (readMore.counting + content.length < readMore.count) {
        readMore.counting = readMore.counting + content.length;

        return content;
    }

    let firstContent = content.substr(0, readMore.count - readMore.counting);
    firstContent = firstContent.substr(0, Math.min(firstContent.length, firstContent.lastIndexOf(' ')));
    const secondContent = content.substr(firstContent.length, content.length);
    readMore.counting = readMore.count;

    return `<span>${firstContent}</span><span class="js-dk-read-more__content">${secondContent}</span>`;
};

const splitChildren = (parent, readMore) => {
    if (readMore.counting >= readMore.count) {
        addClass(parent, 'js-dk-read-more__content');

        return parent.outerHTML;
    }

    const children = parent.childNodes;
    let content = '';

    [...children].forEach((elm) => {
        if (elm.nodeType === Node.TEXT_NODE) {
            content = content + wrapText(elm, readMore);
        } else {
            content = content + splitChildren(elm, readMore);
        }
    });

    parent.innerHTML = content;

    return parent.outerHTML;
};

const splitReadMore = (readMore) => splitChildren(readMore.element, readMore);

const setBtnLabels = (readMore) => {
    const btnLabels = readMore.element.getAttribute('data-btn-labels');

    if (btnLabels) {
        const labelsArray = btnLabels.split(',');

        readMore.btnShowLabel = labelsArray[0].trim();
        readMore.btnHideLabel = labelsArray[1].trim();
    }
};

const resetAppearance = (readMore) => {
    [...readMore.moreContent].forEach((elm) => addClass(elm, 'u-hidden'));

    addClass(readMore.element, 'dk-read-more--loaded');
    removeClass(readMore.element, 'skeleton');
};

const updateVisibility = (readMore, visibile) => {
    [...readMore.moreContent].forEach((elm) => toggleClass(elm, 'u-hidden', !visibile));

    toggleClass(readMore.btnToggle[0], 'u-hidden', visibile);
    toggleClass(readMore.btnToggle[1], 'u-hidden', !visibile);

    if (readMore.ellipsis.length) {
        toggleClass(readMore.ellipsis[0], 'u-hidden', visibile);
    }

    if (!readMore.toggleOff) {
        addClass(readMore.btn, 'u-hidden');
    }
};

const initEvents = (readMore) => {
    readMore.btnToggle = readMore.element.getElementsByClassName('js-dk-read-more__btn');
    readMore.ellipsis = readMore.element.getElementsByClassName('js-dk-read-more__ellipsis');

    readMore.btnToggle[0].addEventListener('click', (event) => {
        event.preventDefault();
        updateVisibility(readMore, true);
    });

    readMore.btnToggle[1].addEventListener('click', (event) => {
        event.preventDefault();
        updateVisibility(readMore, false);
    });
};

const initReadMore = (readMore) => {
    readMore.moreContent = readMore.element.getElementsByClassName('js-dk-read-more__content');

    if (!readMore.moreContent.length) {
        addClass(readMore.element, 'dk-read-more--loaded');
        removeClass(readMore.element, 'skeleton');

        return;
    }

    let btnShow = ` <button class="js-dk-read-more__btn dk-read-more__btn ${readMore.btnClasses}">${readMore.btnShowLabel}</button>`;
    const btnHide = ` <button class="js-dk-read-more__btn dk-read-more__btn u-hidden ${readMore.btnClasses}">${readMore.btnHideLabel}</button>`;

    if (readMore.ellipsis) {
        btnShow = `<span class="js-dk-read-more__ellipsis" aria-hidden="true">...</span>${btnShow}`;
    }

    readMore.moreContent[readMore.moreContent.length - 1].insertAdjacentHTML('afterend', btnHide);
    readMore.moreContent[0].insertAdjacentHTML('afterend', btnShow);

    resetAppearance(readMore);
    initEvents(readMore);
};

/**
 * ReadMore component.
 *
 * Aa truncated paragraph with theoption of reading more content.
 */
export const ReadMore = class {
    /**
     * Create a ReadMore component.
     *
     * @param {HTMLElement} element - ReadMore element
     */
    constructor(element) {
        this.element = element;
        this.moreContent = this.element.getElementsByClassName('js-dk-read-more__content');
        this.count = parseInt(this.element.getAttribute('data-characters'), 10) || 200;
        this.counting = 0;
        this.btnClasses = this.element.getAttribute('data-btn-class');
        this.ellipsis = !(
            this.element.getAttribute('data-ellipsis') && this.element.getAttribute('data-ellipsis') === 'off'
        );
        this.toggleOff = !(
            this.element.getAttribute('data-toggle') && this.element.getAttribute('data-toggle') === 'off'
        );
        this.btnShowLabel = 'Read more';
        this.btnHideLabel = 'Read less';
    }

    /**
     * Initialize the ReadMore component.
     */
    init() {
        if (this.element.classList.contains('js-dk-read-more--is-init')) {
            return;
        }

        if (!this.moreContent.length) {
            splitReadMore(this);
        }

        setBtnLabels(this);
        initReadMore(this);
        this.element.classList.add('js-dk-read-more--is-init');
    }
};

export default (opts) => {
    const options = {
        context: document,
        ...opts,
    };

    return {
        init() {
            if (typeof options.context.getElementsByClassName !== 'function') {
                return;
            }

            const readMore = options.context.getElementsByClassName('js-dk-read-more');

            if (readMore.length) {
                [...readMore].forEach((elm) => new ReadMore(elm).init());
            }
        },
    };
};
